<script lang="ts">
	import type { ComponentProps } from "svelte";
	import newsPlaceholder from "@core/assets/images/news-placeholder.svg";
	import type { Product } from "@core/schema/Product.js";
	import type { ProductGallery } from "@core/schema/ProductGallery.js";
	import type { HoverZoomOptions } from "@core/utils/hoverZoom.js";
	import { makeInflect } from "@core/utils/makeInflect.js";
	import Lightbox from "./common/Lightbox.svelte";
	import Media from "./common/Media.svelte";
	import type { MediaSrc } from "./common/MediaSrc.js";

	const { product }: { product: Product } = $props();
	let currentIndex = $state(0);

	const shownGalleryItem = $derived(product.gallery[currentIndex]);

	const makeMoreLabel = makeInflect("další", "další", "dalších");

	const visibleGalleryItems = 4;
	const visibleThumbnails = $derived(product.gallery.slice(1, 1 + visibleGalleryItems));
	const invisibleThumbnails = $derived(product.gallery.slice(1 + visibleGalleryItems));

	function getSource(galleryItem: ProductGallery | undefined): MediaSrc {
		if (galleryItem) {
			const { youtube, photo } = galleryItem;
			if (youtube) {
				return { youtube };
			}
			if (photo) {
				return photo;
			}
		}
		return {
			src: newsPlaceholder,
			type: "image/svg+xml",
		};
	}

	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<div class="bg-white/50 p-4 backdrop-blur-[8px] w-[42rem] max-w-full rounded-3xl">
	<div class="relative size-full">
		<button
			class="border-light aspect-[4/3] size-full overflow-hidden rounded-lg min-w-"
			onclick={() => {
				window.fsLightboxInstances[product.id]?.open(currentIndex);
			}}
		>
			<Media
				alt="Banner"
				wrapperClass="size-full"
				height={480}
				source={getSource(shownGalleryItem)}
				class="size-full object-cover"
				width={640}
				lightbox={product.id}
				sourcesets={{
					"<2xl": [480, 0],
					">2xl": [480, 0],
				}}
			/>
		</button>
	</div>

	{#if visibleThumbnails.length > 0}
		<div class="flex items-center gap-2">
			{#each visibleThumbnails as galleryItem, index (galleryItem.id)}
				<button
					type="button"
					class="relative h-20 w-32 overflow-hidden rounded-lg"
					onmouseenter={() => {
						currentIndex = index + 1;
					}}
					onmouseleave={() => {
						currentIndex = 0;
					}}
				>
					<Media
						alt={galleryItem.photo?.title ?? "Obrázek galerie"}
						wrapperClass="size-full"
						class="size-full object-cover"
						height={80}
						width={128}
						source={getSource(galleryItem)}
						lightbox={product.id}
						sourcesets={{
							"<2xl": [80, 128],
							">2xl": [80, 128],
						}}
					/>
				</button>
			{/each}

			{#if invisibleThumbnails.length > 0}
				{@const count = visibleThumbnails.length - invisibleThumbnails.length}
				<button
					type="button"
					class="flex h-20 w-32 flex-col items-end justify-center rounded-lg text-right bg-cmcsecondary font-bold text-white leading-tight p-1 sm:p-4"
					onclick={() => {
						window.fsLightboxInstances[product.id]?.open(invisibleThumbnails.length + 1);
					}}
				>
					+{count}<br />
					<span class="font-normal">{makeMoreLabel(count)}</span>
				</button>
				<div class="hidden" aria-hidden="true">
					{#each invisibleThumbnails as galleryItem (galleryItem.id)}
						{#if galleryItem.photo}
							<Lightbox id={product.id} source={{ width: 1920, media: galleryItem.photo }} type="image" />
						{:else if galleryItem.youtube}
							<Lightbox id={product.id} source={{ href: galleryItem.youtube }} type="youtube" />
						{/if}
					{/each}
				</div>
			{/if}
		</div>
	{/if}
</div>
